import type { SectionProps } from "../context";
import { field } from "../context";
import { BuyLink, CartAdd, KindChip, PlayButton, StoreEmpty, StorePrice, pickProducts, productHref, type StoreProductCard } from "../store";
import { CARD_HOVER, Container, EditorImagePlaceholder, Img, MultiLine, OrderLink, Price, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type ProductItem = SectionProps<"products">["node"]["content"]["items"][number];

function statusLabel(ctx: SectionProps<"products">["ctx"], status: ProductItem["status"]) {
  const en = ctx.site.locale === "en-US";
  if (status === "sold") return en ? "Sold" : "Terjual";
  if (status === "reserved") return en ? "Reserved" : "Dipesan";
  return null;
}

/** Artist under the title (artworks, handmade goods). */
function Artist({ ctx, item, p, className }: { ctx: SectionProps<"products">["ctx"]; item: ProductItem; p: string; className?: string }) {
  if (!item.artist) return null;
  return (
    <p className={cx("text-sm text-site-muted", className)} {...field(ctx, `${p}.artist`)}>
      {item.artist}
    </p>
  );
}

/** Medium · size · year: a spec list on cards, one compact line on the grid. */
function Specs({ ctx, item, p, compact }: { ctx: SectionProps<"products">["ctx"]; item: ProductItem; p: string; compact?: boolean }) {
  const en = ctx.site.locale === "en-US";
  const rows = (
    [
      ["medium", en ? "Medium" : "Media", item.medium],
      ["size", en ? "Size" : "Ukuran", item.size],
      ["year", en ? "Year" : "Tahun", item.year],
    ] as const
  ).filter(([, , v]) => !!v);
  if (!rows.length) return null;
  if (compact) {
    return (
      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-site-muted">
        {rows.map(([k, , v], i) => (
          <span key={k}>
            {i ? " · " : ""}
            <span {...field(ctx, `${p}.${k}`)}>{v}</span>
          </span>
        ))}
      </p>
    );
  }
  return (
    <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t border-site-border pt-4 text-sm">
      {rows.map(([k, label, v]) => (
        <div key={k} className="contents">
          <dt className="text-site-muted">{label}</dt>
          <dd className="text-site-text" {...field(ctx, `${p}.${k}`)}>
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Products({ node, ctx }: SectionProps<"products">) {
  const c = node.content;
  if (c.dataSource?.type === "store") return <StoreProducts node={node} ctx={ctx} />;
  if (node.variant === "tracks") return <StaticTracks node={node} ctx={ctx} />;
  const cards = node.variant === "cards";
  // Without any photos, a row of empty squares looks broken: render compact text cards instead.
  const showMedia = ctx.editor || c.items.some((item) => !!item.image);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
        <ul className={cx(STACK, STAGGER, cards ? "grid gap-5 @2xl:grid-cols-2 @5xl:grid-cols-3 @3xl:gap-6" : "grid grid-cols-2 gap-x-4 gap-y-8 @3xl:grid-cols-3 @3xl:gap-x-6 @5xl:grid-cols-4")}>
          {c.items.map((item, i) => {
            const p = `content.items.${i}`;
            const status = statusLabel(ctx, item.status);
            const sold = item.status === "sold";
            const priceClass = sold ? "line-through decoration-1 opacity-60" : "";
            const media = !showMedia ? null : (
              <div className={cx("relative overflow-hidden bg-site-surface", cards ? "aspect-[4/3]" : "aspect-square rounded-site-md")}>
                {item.image ? (
                  <Img
                    ctx={ctx}
                    image={item.image}
                    sizes={cards ? "(min-width: 1024px) 360px, (min-width: 672px) 50vw, 100vw" : "(min-width: 1024px) 280px, (min-width: 768px) 33vw, 50vw"}
                    path={`${p}.image`}
                    className="transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
                  />
                ) : ctx.editor ? (
                  <EditorImagePlaceholder ctx={ctx} path={`${p}.image`} />
                ) : (
                  <div aria-hidden="true" className="grid h-full w-full place-items-center bg-site-primary/5 text-4xl text-site-primary/30 baze-heading">
                    {item.name.slice(0, 1)}
                  </div>
                )}
                {item.badge || status ? (
                  <span className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {status ? (
                      <span className={cx("rounded-full px-2.5 py-1 text-xs font-semibold shadow-site-sm", sold ? "bg-site-inverted text-site-on-inverted" : "bg-site-surface text-site-text")}>{status}</span>
                    ) : null}
                    {item.badge ? (
                      <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary shadow-site-sm" {...field(ctx, `${p}.badge`)}>
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </div>
            );
            if (cards) {
              return (
                <li key={i} className={cx("group flex flex-col overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", CARD_HOVER)}>
                  {media}
                  <div className="flex flex-1 flex-col p-5 @3xl:p-6">
                    {!showMedia && (item.badge || status) ? (
                      <span className="mb-3 flex flex-wrap gap-1.5">
                        {status ? <span className="rounded-full bg-site-inverted px-2.5 py-1 text-xs font-semibold text-site-on-inverted">{status}</span> : null}
                        {item.badge ? (
                          <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary" {...field(ctx, `${p}.badge`)}>
                            {item.badge}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <h3 className="baze-heading text-xl leading-snug" {...field(ctx, `${p}.name`)}>
                      {item.name}
                    </h3>
                    <Artist ctx={ctx} item={item} p={p} className="mt-1" />
                    <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-2 text-sm leading-relaxed text-site-muted" />
                    <Specs ctx={ctx} item={item} p={p} />
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
                      <Price value={item.price} note={item.priceNote} className={cx("text-lg text-site-text", priceClass)} />
                      {sold ? null : <OrderLink
                        ctx={ctx}
                        message={c.orderMessage}
                        produk={item.name} cta={item.cta}
                        className="inline-flex min-h-11 items-center gap-2 rounded-site-button bg-site-primary px-4 text-sm font-semibold text-site-on-primary shadow-site-sm transition hover:shadow-site-md"
                      />}
                    </div>
                  </div>
                </li>
              );
            }
            return (
              <li key={i} className={cx("group flex flex-col", !showMedia && "rounded-site-md border border-site-border bg-site-surface p-5")}>
                {media}
                {!showMedia && (item.badge || status) ? (
                  <span className="mb-3 flex flex-wrap gap-1.5">
                    {status ? <span className="rounded-full bg-site-inverted px-2.5 py-1 text-xs font-semibold text-site-on-inverted">{status}</span> : null}
                    {item.badge ? (
                      <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary" {...field(ctx, `${p}.badge`)}>
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <h3 className="mt-3 text-[0.9375rem] leading-snug font-semibold text-site-text @3xl:text-base" {...field(ctx, `${p}.name`)}>
                  {item.name}
                </h3>
                <Artist ctx={ctx} item={item} p={p} className="mt-0.5" />
                <Specs ctx={ctx} item={item} p={p} compact />
                <Price value={item.price} note={item.priceNote} className={cx("mt-1 text-site-text", priceClass)} />
                {sold ? null : (
                  <OrderLink
                    ctx={ctx}
                    message={c.orderMessage}
                    produk={item.name} cta={item.cta}
                    className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-site-button border border-site-border text-sm font-semibold text-site-text transition hover:border-site-primary hover:bg-site-primary hover:text-site-on-primary"
                  />
                )}
              </li>
            );
          })}
        </ul>
      </Container>
    </SectionShell>
  );
}

/** The `tracks` variant with the owner's own items: rows with a price and the order link. */
function StaticTracks({ node, ctx }: SectionProps<"products">) {
  const c = node.content;
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size="narrow">
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
        <ol className={cx(STACK, STAGGER, "divide-y divide-site-border border-y border-site-border")}>
          {c.items.map((item, i) => {
            const p = `content.items.${i}`;
            return (
              <li key={i} className="flex items-center gap-4 py-4">
                <span className="w-6 shrink-0 text-right text-sm text-site-muted tabular-nums">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-site-text" {...field(ctx, `${p}.name`)}>
                    {item.name}
                  </p>
                  {item.description ? (
                    <p className="truncate text-sm text-site-muted" {...field(ctx, `${p}.description`)}>
                      {item.description}
                    </p>
                  ) : null}
                </div>
                <Price value={item.price} note={item.priceNote} className="hidden text-site-text @xl:inline-flex" />
                {item.status === "sold" ? null : (
                  <OrderLink ctx={ctx} message={c.orderMessage} produk={item.name} cta={item.cta} className="inline-flex min-h-11 items-center gap-2 rounded-site-button border border-site-border px-4 text-sm font-semibold text-site-text transition hover:border-site-primary" />
                )}
              </li>
            );
          })}
        </ol>
      </Container>
    </SectionShell>
  );
}

/**
 * Products from the site's Toko. Each card links to its product page and has "Beli" (checkout);
 * while the store cannot take payments the button falls back to the WhatsApp order link, so a
 * visitor never meets a dead button. Nothing to show: the editor explains, the live page skips it.
 */
function StoreProducts({ node, ctx }: SectionProps<"products">) {
  const c = node.content;
  const source = c.dataSource?.type === "store" ? c.dataSource : {};
  const list = pickProducts(ctx.products, source);
  if (!list.length && !ctx.editor) return null;
  const tracks = node.variant === "tracks";
  const cards = node.variant === "cards";
  const books = list.length > 0 && list.every((p) => p.kind === "ebook");
  const aspect = books ? "aspect-[3/4]" : cards ? "aspect-[4/3]" : "aspect-square";
  const action = (p: StoreProductCard, className?: string) =>
    ctx.store ? (
      <span className="relative z-10 flex flex-col items-stretch gap-2">
        <BuyLink ctx={ctx} product={p} className={className} />
        <CartAdd ctx={ctx} product={p} className={className} />
      </span>
    ) : (
      <OrderLink ctx={ctx} message={c.orderMessage} produk={p.title} className={cx("inline-flex min-h-11 items-center justify-center gap-2 rounded-site-button border border-site-border px-4 text-sm font-semibold text-site-text", className)} />
    );
  const name = (p: StoreProductCard, className: string) =>
    ctx.store ? (
      <SiteLink ctx={ctx} href={productHref(p.slug)} className={cx(className, "after:absolute after:inset-0 hover:text-site-primary")}>
        {p.title}
      </SiteLink>
    ) : (
      <span className={className}>{p.title}</span>
    );

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size={tracks ? "narrow" : "default"}>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
        {!list.length ? (
          <div className={STACK}>
            <StoreEmpty ctx={ctx} message="Produk aktif dari Toko tampil di sini. Tambahkan produk di menu Toko → Produk." />
          </div>
        ) : tracks ? (
          <ol className={cx(STACK, STAGGER, "divide-y divide-site-border border-y border-site-border")}>
            {list.map((p) => (
              <li key={p.slug} className="flex items-center gap-3 py-3 @xl:gap-4">
                {p.previewUrl ? <PlayButton ctx={ctx} product={p} /> : <span className="size-11 shrink-0" aria-hidden="true" />}
                {p.cover ? (
                  <span className="relative hidden size-12 shrink-0 overflow-hidden rounded-site-sm bg-site-surface @md:block">
                    <Img ctx={ctx} image={{ src: p.cover, alt: "" }} sizes="48px" target={96} />
                  </span>
                ) : null}
                <div className="relative min-w-0 flex-1">
                  {name(p, "block truncate font-semibold text-site-text")}
                  {p.details?.length ? (
                    <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-site-muted">
                      {p.details.slice(0, 3).map((d) => (
                        <span key={d.label}>
                          {d.label} <span className="font-medium text-site-text">{d.value}</span>
                        </span>
                      ))}
                    </p>
                  ) : p.subtitle ? (
                    <p className="truncate text-sm text-site-muted">{p.subtitle}</p>
                  ) : null}
                </div>
                <StorePrice product={p} className="hidden text-site-text @xl:inline-flex" />
                {action(p, "px-4")}
              </li>
            ))}
          </ol>
        ) : (
          <ul className={cx(STACK, STAGGER, cards ? "grid gap-5 @2xl:grid-cols-2 @5xl:grid-cols-3 @3xl:gap-6" : "grid grid-cols-2 gap-x-4 gap-y-8 @3xl:grid-cols-3 @3xl:gap-x-6 @5xl:grid-cols-4")}>
            {list.map((p) => (
              <li key={p.slug} className={cx("group relative flex flex-col", cards && cx("overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", CARD_HOVER))}>
                <div className={cx("relative overflow-hidden bg-site-surface", aspect, !cards && "rounded-site-md")}>
                  {p.cover ? (
                    <Img ctx={ctx} image={{ src: p.cover, alt: p.title }} sizes={cards ? "(min-width: 1024px) 360px, (min-width: 672px) 50vw, 100vw" : "(min-width: 1024px) 280px, 50vw"} className="transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none" />
                  ) : (
                    <div aria-hidden="true" className="baze-heading grid h-full w-full place-items-center bg-site-primary/5 text-4xl text-site-primary/30">
                      {p.title.slice(0, 1)}
                    </div>
                  )}
                  <span className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {p.badge ? <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary shadow-site-sm">{p.badge}</span> : null}
                    {p.soldOut ? <span className="rounded-full bg-site-inverted px-2.5 py-1 text-xs font-semibold text-site-on-inverted shadow-site-sm">Habis</span> : null}
                  </span>
                  {p.previewUrl ? (
                    <span className="absolute right-3 bottom-3 z-10">
                      <PlayButton ctx={ctx} product={p} />
                    </span>
                  ) : null}
                </div>
                <div className={cx("flex flex-1 flex-col", cards ? "p-5 @3xl:p-6" : "pt-3")}>
                  <KindChip kind={p.kind} className={cx("self-start", !cards && "hidden @3xl:inline-flex")} />
                  {name(p, cx("baze-heading leading-snug text-site-text", cards ? "mt-3 text-xl" : "mt-2 text-[0.9375rem] font-semibold @3xl:text-base"))}
                  {p.subtitle ? <p className={cx("mt-1 text-sm text-site-muted", !cards && "line-clamp-2")}>{p.subtitle}</p> : null}
                  <div className={cx("mt-auto flex flex-wrap items-center gap-3 pt-4", cards ? "justify-between" : "flex-col items-stretch")}>
                    <StorePrice product={p} className="text-site-text" />
                    {action(p, cards ? undefined : "w-full")}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Container>
    </SectionShell>
  );
}
